/**
 * Image + TTS generation via Vercel AI Gateway (or faux local bytes).
 * Binary assets land in R2; callers store prompt metadata.
 */

import { createGateway } from "@ai-sdk/gateway";
import { experimental_generateVideo as sdkGenerateVideo } from "ai";
import { MEDIA_PINS, useFaux, type MediaGenEnv } from "./env";
import {
  AI_GATEWAY_BASE,
  GatewayError,
  costUsdFromGatewayUsage,
} from "./gateway";
import { ensureWebp, minimalWebpBytes, WEBP_CONTENT_TYPE } from "./image-webp";
import { isRecord } from "./parse-json";

export type GeneratedMedia = {
  bytes: ArrayBuffer;
  contentType: string;
  model: string;
  costUsd: number;
  faux: boolean;
  /** Short text note from the model when present. */
  note?: string;
};

export type GenerateImageOpts = {
  prompt: string;
  referenceImageUrls?: string[];
};

export type GenerateAudioOpts = {
  prompt: string;
  voiceId?: string | null;
  characterHint?: string | null;
  gender?: string | null;
};

export type GenerateVideoOpts = {
  prompt: string;
  durationSec?: number;
  aspectRatio?: string;
  referenceImageBytes?: Uint8Array[];
};

export const VIDEO_DEFAULT_DURATION_SEC = 6;
export const VIDEO_ASPECT_RATIO = "9:16" as const;
export const VIDEO_POLL_INTERVAL_MS = 5_000;
export const VIDEO_POLL_TIMEOUT_MS = 180_000;
export const VIDEO_ALLOWED_DURATIONS = [4, 6, 8] as const;
export const IMAGE_GATEWAY_TIMEOUT_MS = 120_000;
export const AUDIO_GATEWAY_TIMEOUT_MS = 60_000;

function throwIfGatewayAbort(err: unknown, kind: "image" | "audio"): never {
  const name = err instanceof Error ? err.name : "";
  if (name === "AbortError" || name === "TimeoutError") {
    throw new Error(`${kind}_gateway_timeout`);
  }
  const msg = err instanceof Error ? err.message : "fetch_failed";
  throw new Error(`${kind}_gateway_fetch:${msg}`);
}

export type StartGatewayVideoJobOpts = {
  env: MediaGenEnv;
  draftId: string;
  tag: string;
  prompt: string;
  headRefBytes?: Uint8Array[];
  durationSec?: number;
  aspectRatio?: string;
};

export type StartGatewayVideoJobResult = {
  jobId: string;
  operation?: string;
};

export type StatusGatewayVideoJobOpts = {
  env: MediaGenEnv;
  tag: string;
  jobId?: string;
  operation?: string;
};

export type GatewayVideoStatus =
  | { status: "pending" }
  | { status: "completed"; bytes: ArrayBuffer; contentType: string }
  | { status: "failed"; error: string };

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

export function videoIdempotencyKey(draftId: string, tag: string): string {
  return `${draftId}:${tag}`;
}

/** @deprecated Prefer minimalWebpBytes; kept for tests that sniff PNG magic. */
export function minimalPngBytes(): ArrayBuffer {
  const b64 =
    "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";
  return base64ToArrayBuffer(b64);
}

/** Minimal silent WAV (~0.25s mono 8kHz). */
export function minimalWavBytes(durationSec = 0.25): ArrayBuffer {
  const sampleRate = 8000;
  const numSamples = Math.max(1, Math.floor(sampleRate * durationSec));
  const dataSize = numSamples * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);
  return buffer;
}

/** Tiny ftyp+moov stub so local create can skip a real video call. */
export function minimalMp4Bytes(): ArrayBuffer {
  const hex =
    "000000186674797069736F6D0000000169736F6D000000086D6F6F76";
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes.buffer;
}

export function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const clean = b64.replace(/^data:[^;]+;base64,/, "").replace(/\s/g, "");
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/**
 * Generate an image from a text prompt, optionally with reference image URLs
 * (character head refs for consistency).
 */
export async function generateImage(
  env: MediaGenEnv,
  opts: GenerateImageOpts,
): Promise<GeneratedMedia> {
  const model = MEDIA_PINS.gen_image;
  if (useFaux(env)) {
    return {
      bytes: minimalWebpBytes(),
      contentType: WEBP_CONTENT_TYPE,
      model,
      costUsd: 0,
      faux: true,
      note: `faux_image:${opts.prompt.slice(0, 80)}`,
    };
  }

  const apiKey = env.AI_GATEWAY_API_KEY?.trim();
  if (!apiKey) throw new Error("AI_GATEWAY_API_KEY not configured");

  const content: Array<
    | { type: "text"; text: string }
    | { type: "image_url"; image_url: { url: string } }
  > = [{ type: "text", text: opts.prompt }];
  for (const url of opts.referenceImageUrls ?? []) {
    content.push({ type: "image_url", image_url: { url } });
  }

  const body = {
    model,
    messages: [
      {
        role: "user",
        content,
      },
    ],
    modalities: ["text", "image"],
    stream: false,
    generationConfig: {
      responseModalities: ["TEXT", "IMAGE"],
    },
    providerOptions: {
      google: {
        responseModalities: ["TEXT", "IMAGE"],
      },
    },
  };

  let res: Response;
  try {
    res = await fetch(`${AI_GATEWAY_BASE}/v1/chat/completions`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(IMAGE_GATEWAY_TIMEOUT_MS),
    });
  } catch (err) {
    throwIfGatewayAbort(err, "image");
  }
  if (!res.ok) {
    const errBody = await res.text().catch(() => "");
    throw new GatewayError(res.status, errBody);
  }

  let json: GatewayImageResponse;
  try {
    json = (await res.json()) as GatewayImageResponse;
  } catch (err) {
    const msg = err instanceof Error ? err.message : "json_parse_failed";
    throw new Error(`image_gateway_json:${msg}`);
  }

  let extracted: Awaited<ReturnType<typeof resolveImageFromChatResponse>>;
  try {
    extracted = await resolveImageFromChatResponse(json);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "extract_failed";
    throw new Error(`image_extract:${msg}`);
  }
  if (!extracted) {
    const hint = refusalHintFromChatResponse(json);
    throw new Error(
      hint ? `image_generation_empty:${hint}` : "image_generation_empty",
    );
  }
  const costUsd = costUsdFromGatewayUsage(json.usage);
  let webp: Awaited<ReturnType<typeof ensureWebp>>;
  try {
    webp = await ensureWebp(env, extracted.bytes);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "webp_encode_failed";
    throw new Error(`webp_encode:${msg}`);
  }
  return {
    bytes: webp.bytes,
    contentType: webp.contentType,
    model,
    costUsd,
    faux: false,
    note: extracted.note,
  };
}

/**
 * Voices accepted by AI Gateway openai/tts-1.
 * Note: `ballad` is listed on some OpenAI docs but the gateway rejects it
 * (enum: nova, shimmer, echo, onyx, fable, alloy, ash, sage, coral).
 */
export const OPENAI_TTS_VOICES = [
  "alloy",
  "ash",
  "coral",
  "echo",
  "fable",
  "nova",
  "onyx",
  "sage",
  "shimmer",
] as const;

export type OpenAiTtsVoice = (typeof OPENAI_TTS_VOICES)[number];

const OPENAI_TTS_VOICE_SET = new Set<string>(OPENAI_TTS_VOICES);

/** Prefer lower / darker for male-coded, brighter for female-coded. */
const MALE_VOICES: OpenAiTtsVoice[] = ["onyx", "echo", "ash", "fable"];
const FEMALE_VOICES: OpenAiTtsVoice[] = [
  "nova",
  "shimmer",
  "coral",
  "sage",
  "alloy",
];

/**
 * Prefer quoted dialogue in the asset description; otherwise speak the
 * description itself (covers plan text without embedded quotes).
 */
export function spokenScriptFromDescription(description: string): string {
  const quotes = [...description.matchAll(/"([^"]{2,})"/g)].map((m) =>
    m[1].trim(),
  );
  if (quotes.length > 0) return quotes.join(" ");
  const trimmed = description.trim();
  if (!trimmed) return "Hello.";
  return trimmed;
}

function hashSeed(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Map case-local voice ids (`voice_li_ke_…`) to a stable OpenAI voice.
 * Gender (when known) picks the pool; seed keeps the same character consistent.
 */
export function resolveOpenAiTtsVoice(opts: {
  voiceId?: string | null;
  characterHint?: string | null;
  gender?: string | null;
}): OpenAiTtsVoice {
  const raw = opts.voiceId?.trim().toLowerCase() ?? "";
  if (raw && OPENAI_TTS_VOICE_SET.has(raw)) {
    return raw as OpenAiTtsVoice;
  }

  const gender = (opts.gender ?? "").trim().toLowerCase();
  const pool =
    gender === "male" || gender === "m" || gender === "man"
      ? MALE_VOICES
      : gender === "female" || gender === "f" || gender === "woman"
        ? FEMALE_VOICES
        : OPENAI_TTS_VOICES;

  const seed = hashSeed(
    [opts.characterHint, opts.voiceId].filter(Boolean).join("|") || "default",
  );
  return pool[seed % pool.length] ?? "alloy";
}

/**
 * Matches @ai-sdk/gateway speech client protocol headers.
 * Bare `ai-speech-model-specification-version: 4` without the gateway
 * protocol header returns 400 "Unsupported gateway protocol version".
 */
const AI_GATEWAY_PROTOCOL_VERSION = "0.0.1";
const AI_SPEECH_MODEL_SPEC_VERSION = "4";

/**
 * Generate TTS audio for a character line / voice memo description.
 * Uses Vercel AI Gateway speech models (openai/tts-1).
 *
 * Prefer OpenAI-compatible `/v1/audio/speech` (binary), then fall back to
 * `/v4/ai/speech-model` with AI SDK protocol headers (base64 JSON).
 */
export async function generateAudio(
  env: MediaGenEnv,
  opts: GenerateAudioOpts,
): Promise<GeneratedMedia> {
  const model = MEDIA_PINS.gen_audio;
  const spokenText = spokenScriptFromDescription(opts.prompt);
  const voice = resolveOpenAiTtsVoice(opts);

  if (useFaux(env)) {
    return {
      bytes: minimalWavBytes(0.5),
      contentType: "audio/wav",
      model,
      costUsd: 0,
      faux: true,
      note: `faux_audio:${voice}:${spokenText.slice(0, 80)}`,
    };
  }

  const apiKey = env.AI_GATEWAY_API_KEY?.trim();
  if (!apiKey) throw new Error("AI_GATEWAY_API_KEY not configured");

  const outputFormat = "mp3";

  let openaiCompat: Response;
  try {
    openaiCompat = await fetch(`${AI_GATEWAY_BASE}/v1/audio/speech`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model,
        input: spokenText,
        voice,
        response_format: outputFormat,
      }),
      signal: AbortSignal.timeout(AUDIO_GATEWAY_TIMEOUT_MS),
    });
  } catch (err) {
    throwIfGatewayAbort(err, "audio");
  }
  if (openaiCompat.ok) {
    const contentTypeHeader = openaiCompat.headers.get("content-type") || "";
    if (
      contentTypeHeader.includes("audio/") ||
      contentTypeHeader.includes("application/octet-stream")
    ) {
      const bytes = await openaiCompat.arrayBuffer();
      if (bytes.byteLength === 0) throw new Error("audio_generation_empty");
      return {
        bytes,
        contentType:
          contentTypeHeader.split(";")[0].trim() ||
          contentTypeForFormat(outputFormat),
        model,
        costUsd: 0,
        faux: false,
        note: `voice=${voice};via=openai_audio_speech`,
      };
    }
    const json = (await openaiCompat.json().catch(() => null)) as {
      audio?: string;
      usage?: { total_cost?: number };
    } | null;
    if (json && typeof json.audio === "string" && json.audio) {
      const bytes = base64ToArrayBuffer(json.audio);
      if (bytes.byteLength === 0) throw new Error("audio_generation_empty");
      return {
        bytes,
        contentType: contentTypeForFormat(outputFormat),
        model,
        costUsd: costUsdFromGatewayUsage(json.usage),
        faux: false,
        note: `voice=${voice};via=openai_audio_speech_json`,
      };
    }
  }
  const openaiCompatErr = openaiCompat.ok
    ? "ok_but_unparsed"
    : (await openaiCompat.text().catch(() => "")).slice(0, 240);

  let speechRes: Response;
  try {
    speechRes = await fetch(`${AI_GATEWAY_BASE}/v4/ai/speech-model`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
        "ai-model-id": model,
        "ai-gateway-protocol-version": AI_GATEWAY_PROTOCOL_VERSION,
        "ai-speech-model-specification-version": AI_SPEECH_MODEL_SPEC_VERSION,
      },
      body: JSON.stringify({
        text: spokenText,
        voice,
        outputFormat,
      }),
      signal: AbortSignal.timeout(AUDIO_GATEWAY_TIMEOUT_MS),
    });
  } catch (err) {
    throwIfGatewayAbort(err, "audio");
  }

  if (!speechRes.ok) {
    const errBody = await speechRes.text().catch(() => "");
    throw new GatewayError(
      speechRes.status,
      `speech_model:${errBody.slice(0, 300)}; openai_audio_speech:${openaiCompatErr}`,
    );
  }

  const contentTypeHeader = speechRes.headers.get("content-type") || "";
  if (
    contentTypeHeader.includes("audio/") ||
    contentTypeHeader.includes("application/octet-stream")
  ) {
    const bytes = await speechRes.arrayBuffer();
    if (bytes.byteLength === 0) throw new Error("audio_generation_empty");
    return {
      bytes,
      contentType:
        contentTypeHeader.split(";")[0].trim() ||
        contentTypeForFormat(outputFormat),
      model,
      costUsd: 0,
      faux: false,
      note: `voice=${voice};via=speech_model_binary`,
    };
  }

  const json = (await speechRes.json()) as {
    audio?: string;
    warnings?: unknown;
    usage?: { total_cost?: number };
  };
  if (typeof json.audio !== "string" || !json.audio) {
    throw new Error("audio_generation_empty");
  }
  const bytes = base64ToArrayBuffer(json.audio);
  if (bytes.byteLength === 0) throw new Error("audio_generation_empty");
  return {
    bytes,
    contentType: contentTypeForFormat(outputFormat),
    model,
    costUsd: costUsdFromGatewayUsage(json.usage),
    faux: false,
    note: `voice=${voice};via=speech_model`,
  };
}

export function clampVideoDurationSec(durationSec?: number): 4 | 6 | 8 {
  if (durationSec == null || !Number.isFinite(durationSec)) {
    return VIDEO_DEFAULT_DURATION_SEC;
  }
  if (durationSec <= 4) return 4;
  if (durationSec >= 8) return 8;
  return 6;
}

function videoAspectRatio(
  aspectRatio?: string,
): typeof VIDEO_ASPECT_RATIO | "16:9" {
  return aspectRatio === "16:9" ? "16:9" : VIDEO_ASPECT_RATIO;
}

function toMediaGenEnvKey(env: MediaGenEnv): string {
  const key = env.AI_GATEWAY_API_KEY?.trim();
  if (!key) throw new Error("AI_GATEWAY_API_KEY not configured");
  return key;
}

function gatewayVideoModel(env: MediaGenEnv) {
  const gateway = createGateway({ apiKey: toMediaGenEnvKey(env) });
  return gateway.videoModel(MEDIA_PINS.gen_video);
}

function videoInputReferences(bytes?: Uint8Array[]): Array<{
  type: "file";
  mediaType: string;
  data: Uint8Array;
}> {
  return (bytes ?? []).map((data) => ({
    type: "file" as const,
    mediaType: "image/webp",
    data,
  }));
}

function uint8ToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function jobIdFromStart(started: {
  operation?: unknown;
  providerMetadata?: unknown;
}, tag: string): string {
  const meta = started.providerMetadata;
  if (isRecord(meta) && isRecord(meta.gateway) && isRecord(meta.gateway.asyncJob)) {
    const jobId = meta.gateway.asyncJob.jobId;
    if (typeof jobId === "string" && jobId.trim()) return jobId;
  }
  return tag;
}

function parseStoredOperation(raw?: string): JsonValue | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as JsonValue;
  } catch {
    return raw;
  }
}

function toJsonValue(value: unknown): JsonValue {
  return JSON.parse(JSON.stringify(value)) as JsonValue;
}

function emptyProviderOptions(): { [key: string]: { [key: string]: JsonValue } } {
  return {};
}

function operationFromStatusOpts(
  opts: StatusGatewayVideoJobOpts,
): JsonValue | undefined {
  return parseStoredOperation(opts.operation);
}

async function videoPayloadToBytes(video: unknown): Promise<{
  bytes: ArrayBuffer;
  contentType: string;
} | null> {
  if (!isRecord(video)) return null;
  const mediaType =
    typeof video.mediaType === "string" && video.mediaType
      ? video.mediaType
      : "video/mp4";
  if (video.type === "base64" && typeof video.data === "string" && video.data) {
    const bytes = base64ToArrayBuffer(video.data);
    if (bytes.byteLength === 0) return null;
    return { bytes, contentType: mediaType };
  }
  if (typeof video.data === "string" && video.data && video.type !== "url") {
    const bytes = base64ToArrayBuffer(video.data);
    if (bytes.byteLength === 0) return null;
    return { bytes, contentType: mediaType };
  }
  const url = typeof video.url === "string" ? video.url : undefined;
  if (url) {
    const res = await fetch(url);
    if (!res.ok) return null;
    const bytes = await res.arrayBuffer();
    if (bytes.byteLength === 0) return null;
    const contentType =
      res.headers.get("content-type")?.split(";")[0].trim() || mediaType;
    return { bytes, contentType };
  }
  if (video.uint8Array instanceof Uint8Array) {
    return {
      bytes: uint8ToArrayBuffer(video.uint8Array),
      contentType: mediaType,
    };
  }
  return null;
}

function fauxVideoMedia(): GeneratedMedia {
  return {
    bytes: minimalMp4Bytes(),
    contentType: "video/mp4",
    model: MEDIA_PINS.gen_video,
    costUsd: 0,
    faux: true,
    note: "faux_video",
  };
}

export async function generateVideo(
  env: MediaGenEnv,
  opts: GenerateVideoOpts,
): Promise<GeneratedMedia> {
  const modelId = MEDIA_PINS.gen_video;
  if (useFaux(env)) {
    return {
      ...fauxVideoMedia(),
      note: `faux_video:${opts.prompt.slice(0, 80)}`,
    };
  }

  const model = gatewayVideoModel(env);
  const duration = clampVideoDurationSec(opts.durationSec);
  const aspectRatio = videoAspectRatio(opts.aspectRatio);
  const references = (opts.referenceImageBytes ?? []).map((data) => ({
    data,
    mediaType: "image/webp",
  }));
  const result = await sdkGenerateVideo({
    model,
    prompt: opts.prompt,
    duration,
    aspectRatio,
    inputReferences: references.length > 0 ? references : undefined,
    poll: {
      intervalMs: VIDEO_POLL_INTERVAL_MS,
      timeoutMs: VIDEO_POLL_TIMEOUT_MS,
    },
  });
  const video = result.videos[0] ?? result.video;
  if (!video || video.uint8Array.byteLength === 0) {
    throw new Error("video_generation_empty");
  }
  return {
    bytes: uint8ToArrayBuffer(video.uint8Array),
    contentType: video.mediaType || "video/mp4",
    model: modelId,
    costUsd: 0,
    faux: false,
  };
}

export async function startGatewayVideoJob(
  opts: StartGatewayVideoJobOpts,
): Promise<StartGatewayVideoJobResult> {
  const idempotencyKey = videoIdempotencyKey(opts.draftId, opts.tag);
  if (useFaux(opts.env)) {
    const jobId = `faux:${idempotencyKey}`;
    const operation = JSON.stringify({
      faux: true,
      draftId: opts.draftId,
      tag: opts.tag,
    });
    return { jobId, operation };
  }

  const model = gatewayVideoModel(opts.env);
  if (!model.doStart) throw new Error("video_do_start_unavailable");
  const duration = clampVideoDurationSec(opts.durationSec);
  const aspectRatio = videoAspectRatio(opts.aspectRatio);
  const inputReferences = videoInputReferences(opts.headRefBytes);
  const started = await model.doStart({
    prompt: opts.prompt,
    n: 1,
    aspectRatio,
    resolution: undefined,
    duration,
    fps: undefined,
    seed: undefined,
    image: undefined,
    frameImages: undefined,
    inputReferences: inputReferences.length > 0 ? inputReferences : undefined,
    generateAudio: undefined,
    providerOptions: emptyProviderOptions(),
    headers: { "idempotency-key": idempotencyKey },
  });
  const jobId = jobIdFromStart(started, idempotencyKey);
  return {
    jobId,
    operation: JSON.stringify(started.operation ?? { jobId }),
  };
}

export async function statusGatewayVideoJob(
  opts: StatusGatewayVideoJobOpts,
): Promise<GatewayVideoStatus> {
  if (useFaux(opts.env)) {
    return {
      status: "completed",
      bytes: minimalMp4Bytes(),
      contentType: "video/mp4",
    };
  }

  const operation = operationFromStatusOpts(opts);
  if (operation == null) {
    return { status: "failed", error: "video_job_missing" };
  }
  if (isRecord(operation) && operation.faux === true) {
    return {
      status: "completed",
      bytes: minimalMp4Bytes(),
      contentType: "video/mp4",
    };
  }

  const model = gatewayVideoModel(opts.env);
  if (!model.doStatus) throw new Error("video_do_status_unavailable");
  const st = await model.doStatus({ operation: toJsonValue(operation) });
  if (st.status === "pending") return { status: "pending" };
  if (st.status !== "completed") {
    const err =
      "error" in st && typeof st.error === "string"
        ? st.error
        : `video_failed:${opts.tag}`;
    return { status: "failed", error: err };
  }
  const payload = await videoPayloadToBytes(st.videos[0]);
  if (!payload) {
    return { status: "failed", error: "video_generation_empty" };
  }
  return {
    status: "completed",
    bytes: payload.bytes,
    contentType: payload.contentType,
  };
}

function contentTypeForFormat(format: string): string {
  switch (format) {
    case "mp3":
      return "audio/mpeg";
    case "wav":
      return "audio/wav";
    case "opus":
      return "audio/opus";
    case "aac":
      return "audio/aac";
    case "flac":
      return "audio/flac";
    default:
      return "audio/mpeg";
  }
}

type GatewayImageResponse = {
  choices?: Array<{
    message?: {
      content?: unknown;
      images?: Array<{
        type?: string;
        image_url?: { url?: string };
        b64_json?: string;
        url?: string;
      }>;
    };
    finish_reason?: string | null;
  }>;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    input_tokens?: number;
    output_tokens?: number;
    total_cost?: number;
  };
};

type ExtractedImageRef =
  | { kind: "bytes"; bytes: ArrayBuffer; contentType: string; note?: string }
  | { kind: "url"; url: string; note?: string };

/** Visible for tests. Pull image bytes or remote URL from a chat completion. */
export function extractImageFromChatResponse(
  json: GatewayImageResponse,
): ExtractedImageRef | null {
  const msg = json.choices?.[0]?.message;
  if (!msg) return null;

  if (Array.isArray(msg.images)) {
    for (const img of msg.images) {
      if (img.b64_json) {
        return {
          kind: "bytes",
          bytes: base64ToArrayBuffer(img.b64_json),
          contentType: "image/png",
        };
      }
      const url = img.image_url?.url ?? img.url;
      if (typeof url === "string" && url.length > 0) {
        if (url.startsWith("data:")) {
          const semi = url.indexOf(";");
          const ct = semi > 5 ? url.slice(5, semi) : "image/png";
          return {
            kind: "bytes",
            bytes: base64ToArrayBuffer(url),
            contentType: ct || "image/png",
          };
        }
        if (url.startsWith("http://") || url.startsWith("https://")) {
          return { kind: "url", url };
        }
      }
    }
  }

  const content = msg.content;
  if (Array.isArray(content)) {
    for (const part of content) {
      if (!part || typeof part !== "object") continue;
      const p = part as Record<string, unknown>;
      if (
        (p.type === "image_url" || p.type === "image") &&
        p.image_url &&
        typeof p.image_url === "object"
      ) {
        const url = (p.image_url as { url?: string }).url;
        if (typeof url === "string" && url.length > 0) {
          if (url.startsWith("data:")) {
            const semi = url.indexOf(";");
            const ct = semi > 5 ? url.slice(5, semi) : "image/png";
            return {
              kind: "bytes",
              bytes: base64ToArrayBuffer(url),
              contentType: ct || "image/png",
            };
          }
          if (url.startsWith("http://") || url.startsWith("https://")) {
            return { kind: "url", url };
          }
        }
      }
      if (typeof p.b64_json === "string") {
        return {
          kind: "bytes",
          bytes: base64ToArrayBuffer(p.b64_json),
          contentType: "image/png",
        };
      }
      if (typeof p.inlineData === "object" && p.inlineData) {
        const data = (p.inlineData as { data?: string; mimeType?: string }).data;
        const mime =
          (p.inlineData as { mimeType?: string }).mimeType || "image/png";
        if (data) {
          return {
            kind: "bytes",
            bytes: base64ToArrayBuffer(data),
            contentType: mime,
          };
        }
      }
      if (typeof p.inline_data === "object" && p.inline_data) {
        const data = (p.inline_data as { data?: string; mime_type?: string })
          .data;
        const mime =
          (p.inline_data as { mime_type?: string }).mime_type || "image/png";
        if (data) {
          return {
            kind: "bytes",
            bytes: base64ToArrayBuffer(data),
            contentType: mime,
          };
        }
      }
    }
  }

  if (typeof content === "string") {
    const dataUri = content.match(
      /data:image\/[a-zA-Z0-9.+-]+;base64,[A-Za-z0-9+/=]+/,
    );
    if (dataUri) {
      const ct = dataUri[0].slice(5, dataUri[0].indexOf(";"));
      return {
        kind: "bytes",
        bytes: base64ToArrayBuffer(dataUri[0]),
        contentType: ct || "image/png",
        note: content.slice(0, 200),
      };
    }
  }

  return null;
}

async function resolveImageFromChatResponse(
  json: GatewayImageResponse,
): Promise<{ bytes: ArrayBuffer; contentType: string; note?: string } | null> {
  const extracted = extractImageFromChatResponse(json);
  if (!extracted) return null;
  if (extracted.kind === "bytes") {
    return {
      bytes: extracted.bytes,
      contentType: extracted.contentType,
      note: extracted.note,
    };
  }
  const imgRes = await fetch(extracted.url);
  if (!imgRes.ok) return null;
  const bytes = await imgRes.arrayBuffer();
  if (bytes.byteLength === 0) return null;
  const contentType =
    imgRes.headers.get("content-type")?.split(";")[0].trim() || "image/png";
  return {
    bytes,
    contentType,
    note: extracted.note ?? `fetched:${extracted.url.slice(0, 120)}`,
  };
}

/** Short model text when image payload is missing (refusals, safety). */
export function refusalHintFromChatResponse(
  json: GatewayImageResponse,
): string | null {
  const msg = json.choices?.[0]?.message;
  if (!msg) return null;
  const parts: string[] = [];
  const content = msg.content;
  if (typeof content === "string" && content.trim()) {
    parts.push(content.trim());
  } else if (Array.isArray(content)) {
    for (const part of content) {
      if (!part || typeof part !== "object") continue;
      const p = part as Record<string, unknown>;
      if (p.type === "text" && typeof p.text === "string" && p.text.trim()) {
        parts.push(p.text.trim());
      }
    }
  }
  const joined = parts.join(" ").replace(/\s+/g, " ").trim();
  if (!joined) return null;
  return joined.slice(0, 220);
}
